/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import CardLayout from "../Cards/CardLayout";
import { useFetchData } from "../../customHooks/useGetDashboardData";
import Loader from "../feedBacks/loader";
import CardError from "../feedBacks/CardError";
import NoContent from "../feedBacks/NoContent";
import { COLORS, REFETCH_CONFIGURATION } from "../../utils/constants";
import { EChartsOption } from "echarts";
import { useAppSelector } from "../../store/hooks";
import { createToolboxConfig } from "../../utils/utils";
import NoDeviceCardError from "../feedBacks/NoDeviceCardError";
import { IGeneralChartProps } from "../../utils/interfaces";
import { useUtils } from "../../customHooks/useUtils";

// Interface for the two possible response formats
interface ILoadProfileDataObject {
  dataset: {
    timestamp: string;
    avg: number;
    min: number;
    max: number;
  }[];
}

interface ILoadProfileDataArray {
  dataset: [string, number][];
}
const filter = [
  { key: "7 Days", value: 7 },
  { key: "30 Days", value: 30 },
 
];

// Combined type
type LoadProfileData = ILoadProfileDataObject | ILoadProfileDataArray;

const LoadProfileChart = ({
  queryKey,
  route,
  params = {},
  title = "",
  isPredicted = false,
}: IGeneralChartProps) => {
  const { FEEDBACK_MESSAGES, t } = useUtils();
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const startDate = useAppSelector((state) => state.filter.startDate);
  const endDate = useAppSelector((state) => state.filter.endDate);
  const todayDate = new Date().toISOString().split("T")[0];
  const predictedEndDate = useMemo(() => {
    if (isPredicted) {
      const date = new Date(todayDate);
      date.setDate(date.getDate() + 7); 
      return date.toISOString().split("T")[0];
    }
    return endDate;
  }, [isPredicted, endDate, todayDate]);

  const {
    data,
    isLoading: isDataLoading,
    error,
    refetch,
  } = useFetchData(
    queryKey,
    route,
    {
      ...params,
      start_date: isPredicted? todayDate : startDate,
      end_date: isPredicted ? predictedEndDate : endDate,
    },
    // @ts-ignore
    {
      ...REFETCH_CONFIGURATION,
    }
  );

  // Determine data format and extract accordingly
  const { loadProfileData, isAggregatedData } = useMemo(() => {
    if (!data) return { loadProfileData: [], isAggregatedData: false };

    // Check if data is in the object format (with timestamp, avg, min, max)
    const dataset = (data as LoadProfileData).dataset;

    if (Array.isArray(dataset)) {
      // Check first item to determine format
      if (dataset.length > 0) {
        const firstItem = dataset[0];

        // If it's an object with timestamp, avg, min, max properties
        if (
          typeof firstItem === "object" &&
          firstItem !== null &&
          "timestamp" in firstItem
        ) {
          return {
            loadProfileData: dataset,
            isAggregatedData: true,
          };
        }

        // If it's an array with just timestamp and value
        if (Array.isArray(firstItem)) {
          return {
            loadProfileData: dataset,
            isAggregatedData: false,
          };
        }
      }
    }

    return { loadProfileData: [], isAggregatedData: false };
  }, [data]);

  // Determine if the data is daily format (YYYY-MM-DD) or has time components
  const isDateOnly = useMemo(() => {
    if (loadProfileData.length === 0) return false;

    const firstTimestamp = isAggregatedData
      ? (loadProfileData[0] as any).timestamp
      : (loadProfileData[0] as any)[0];

    // Check if the timestamp is in YYYY-MM-DD format (no time component)
    return /^\d{4}-\d{2}-\d{2}$/.test(firstTimestamp);
  }, [loadProfileData, isAggregatedData]);

  // Format dates for better display based on the data format
  const formattedData = useMemo(() => {
    if (isAggregatedData) {
      // For aggregated data with timestamp, avg, min, max properties
      return loadProfileData.map((item: any) => {
        const date = new Date(item.timestamp);

        // Format date string
        let formattedDate;
        if (isDateOnly) {
          formattedDate = `${date.toLocaleString("default", {
            month: "short",
          })} ${date.getDate()}`;
        } else {
          formattedDate = `${date.toLocaleString("default", {
            month: "short",
          })} ${date.getDate()} ${String(date.getHours()).padStart(
            2,
            "0"
          )}:${String(date.getMinutes()).padStart(2, "0")}`;
        }

        return [formattedDate, item.avg, item.min, item.max];
      });
    } else {
      // For simple array data with just timestamp and value
      return loadProfileData.map((item: any) => {
        const timestamp = item[0];
        const value = item[1];
        const date = new Date(timestamp);

        // Format date string
        let formattedDate;
        if (isDateOnly) {
          formattedDate = `${date.toLocaleString("default", {
            month: "short",
          })} ${date.getDate()}`;
        } else {
          formattedDate = `${date.toLocaleString("default", {
            month: "short",
          })} ${date.getDate()} ${String(date.getHours()).padStart(
            2,
            "0"
          )}:${String(date.getMinutes()).padStart(2, "0")}`;
        }

        return [formattedDate, value];
      });
    }
  }, [loadProfileData, isDateOnly, isAggregatedData]);

  // Determine the time difference between data points
  const timeGranularity = useMemo(() => {
    if (loadProfileData.length < 2) return "unknown";

    // For date-only data, assume it's daily
    if (isDateOnly) return "days";

    // Get timestamps based on data format
    const timestamp1 = isAggregatedData
      ? (loadProfileData[0] as any).timestamp
      : (loadProfileData[0] as any)[0];

    const timestamp2 = isAggregatedData
      ? (loadProfileData[1] as any).timestamp
      : (loadProfileData[1] as any)[0];

    const date1 = new Date(timestamp1);
    const date2 = new Date(timestamp2);

    const diffMinutes = Math.abs(
      (date2.getTime() - date1.getTime()) / (1000 * 60)
    );

    if (diffMinutes < 60) return "minutes";
    if (diffMinutes < 24 * 60) return "hours";
    return "days";
  }, [loadProfileData, isDateOnly, isAggregatedData]);

  // Set interval for x-axis labels based on time granularity and data length
  const determineInterval = useMemo(() => {
    const dataLength = formattedData.length;

    switch (timeGranularity) {
      case "minutes":
        // For minute data, show labels every 15 minutes
        return (index: number, value: string) => {
          if (!value) return false;
          const parts = value.split(" ");
          if (parts.length < 3) return false;
          const timeParts = parts[2].split(":");
          if (timeParts.length < 2) return false;
          const minutes = parseInt(timeParts[1], 10);
          return minutes % 30 === 0;
        };

      case "hours":
        // For hourly data, show labels every hour
        return (index: number, value: string) => {
          if (!value) return false;
          const parts = value.split(" ");
          if (parts.length < 3) return false;
          const timeParts = parts[2].split(":");
          if (timeParts.length < 2) return false;
          const minutes = parseInt(timeParts[1], 10);
          return minutes === 0;
        };

      case "days":
        // For daily data, use numeric interval based on data length
        if (dataLength <= 7) return 0; // Show all labels for a week or less
        if (dataLength <= 31) return 1; // Show every other day for a month
        if (dataLength <= 90) return 2; // Show every third day for quarter
        return Math.floor(dataLength / 30); // Show about 30 labels for longer periods

      default:
        // Default fallback
        return Math.max(Math.floor(dataLength / 30), 0);
    }
  }, [formattedData.length, timeGranularity]);

  const options: EChartsOption = useMemo(() => {
    // Create different chart options based on data format
    if (isAggregatedData) {
      // For aggregated data with avg, min, max
      return {
        ...createToolboxConfig({
          chartName: t("chart.load_profile"),
          top: "2px",
          right: "2px",
        }),
        grid: {
          left: "60px",
          right: "50px",
          bottom: "80px", // Increased bottom padding for rotated labels
          top: "40px",
          containLabel: true,
        },
        dataset: {
          source: [["time", "average", "minimum", "maximum"], ...formattedData],
        },
        tooltip: {
          trigger: "axis",
          formatter: (params: any) => {
            const avgData = params[0];
            const minData = params[1];
            const maxData = params[2];
            return `${avgData.name}<br/>
                    ${t("chart.average_load")}: ${avgData.value[1].toFixed(
              2
            )} kW<br/>
                    ${t("chart.minimum_load")}: ${minData.value[2].toFixed(
              2
            )} kW<br/>
                    ${t("chart.maximum_load")}: ${maxData.value[3].toFixed(
              2
            )} kW`;
          },
        },
        // legend: {
        //   data: ["Average Load", "Minimum Load", "Maximum Load"],
        //   bottom: 40,
        // },
        dataZoom: [
          {
            type: "slider",
            show: true,
            start: 0,
            end: 100,
            height: 30,
            bottom: 5,
            borderColor: "#ccc",
            fillerColor: "rgba(124, 135, 142, 0.2)",
            handleStyle: {
              color: "#7C878E",
              borderColor: "#7C878E",
            },
          },
          {
            type: "inside",
            start: 0,
            end: 100,
          },
        ],
        xAxis: {
          type: "category",
          name: "Time",
          nameLocation: "middle",
          nameGap: 60,
          boundaryGap: false,
          axisLabel: {
            rotate: 45,
            interval: determineInterval,
            margin: 15,
            formatter: (value: string) => value,
            width: 80,
            align: "right",
            overflow: "truncate",
            showMaxLabel: true,
            color: "#333",
            fontSize: 8,
          },
          axisTick: {
            alignWithLabel: true,
            interval: 0,
          },
        },
        yAxis: {
          axisLine: {
            show: true,
            lineStyle: {
              color: "#000",
              width: 0.8,
            },
          },
          type: "value",
          name: "Load (kW)",
          nameLocation: "middle",
          nameGap: 50,
          min: 0,
          splitLine: {
            lineStyle: {
              type: "dashed",
              color: "#E5E5E5",
            },
          },
        },
        series: [
          {
            name: "Average Load",
            type: "line",
            encode: {
              x: "time",
              y: "average",
            },
            smooth: true,
            symbol: "circle",
            symbolSize: 4,
            showSymbol: false, // Only show symbols on hover
            emphasis: {
              scale: true,
              focus: "series",
            },
            areaStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  {
                    offset: 0,
                    color: "rgba(124, 135, 142, 0.5)",
                  }, // gray fill
                  { offset: 1, color: "rgba(124, 135, 142, 0)" },
                ],
              },
            },
            lineStyle: {
              color: COLORS.BLACK,
              width: 2,
            },
            z: 3,
          },
          {
            name: "Minimum Load",
            type: "line",
            encode: {
              x: "time",
              y: "minimum",
            },
            smooth: true,
            symbol: "circle",
            symbolSize: 4,
            showSymbol: false,
            emphasis: {
              scale: true,
              focus: "series",
            },
            areaStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: "rgba(255, 205, 0, 0.5)" }, // Yellow fill
                  {
                    offset: 1,
                    color: "rgba(255, 205, 0, 0)",
                  },
                ],
              },
            },
            lineStyle: {
              color: COLORS.YELLOW,
              width: 2,
            },
            z: 2,
          },
          {
            name: "Maximum Load",
            type: "line",
            encode: {
              x: "time",
              y: "maximum",
            },
            smooth: true,
            symbol: "circle",
            symbolSize: 4,
            showSymbol: false,
            emphasis: {
              scale: true,
              focus: "series",
            },
            areaStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: "rgba(0, 122, 255, 0.5)" }, // Blue fill
                  { offset: 1, color: "rgba(0, 122, 255, 0)" },
                ],
              },
            },
            lineStyle: {
              color: "#007AFF",
              width: 2,
            },
            z: 1,
          },
        ],
      };
    } else {
      // For simple data with just value (original implementation)
      return {
        ...createToolboxConfig({
          chartName: t("chart.load_profile"),
          top: "2px",
          right: "2px",
        }),
        grid: {
          left: "60px",
          right: "50px",
          bottom: "80px",
          top: "40px",
          containLabel: true,
        },
        dataset: {
          source: [["time", "profile"], ...formattedData],
        },
        tooltip: {
          trigger: "axis",
          formatter: (params: any) => {
            const data = params[0];
            return `${data.name}<br/>Load: ${data.value[1]} kW`;
          },
        },
        dataZoom: [
          {
            type: "slider",
            show: true,
            start: 0,
            end: 100,
            height: 30,
            bottom: 5,
            borderColor: "#ccc",
            fillerColor: "rgba(124, 135, 142, 0.2)",
            handleStyle: {
              color: "#7C878E",
              borderColor: "#7C878E",
            },
          },
          {
            type: "inside",
            start: 0,
            end: 100,
          },
        ],
        xAxis: {
          type: "category",
          name: t("chart.time_label"),
          nameLocation: "middle",
          nameGap: 60,
          boundaryGap: false,
          axisLabel: {
            rotate: 45,
            interval: determineInterval,
            margin: 15,
            formatter: (value: string) => value,
            width: 80,
            align: "right",
            overflow: "truncate",
            showMaxLabel: true,
            color: "#333",
            fontSize: 8,
          },
          axisTick: {
            alignWithLabel: true,
            interval: 0,
          },
        },
        yAxis: {
          axisLine: {
            show: true,
            lineStyle: {
              color: "#000",
              width: 0.8,
            },
          },
          type: "value",
          name: t("chart.load_profile_label"),
          nameLocation: "middle",
          nameGap: 50,
          min: 0,
          splitLine: {
            lineStyle: {
              type: "dashed",
              color: "#E5E5E5",
            },
          },
        },
        series: [
          {
            name: "Profile",
            type: "line",
            encode: {
              x: "time",
              y: "profile",
            },
            smooth: true,
            symbol: "circle",
            symbolSize: 4,
            showSymbol: false,
            emphasis: {
              scale: true,
              focus: "series",
            },
            areaStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: "#7C878E" },
                  { offset: 1, color: "#7C878E00" },
                ],
              },
            },
            lineStyle: {
              color: "#000000",
              width: 2,
            },
          },
        ],
      };
    }
  }, [formattedData, determineInterval, isAggregatedData, t]);

  const isLoading =
    // Show loading for initial page load
    isDataLoading ||
    // Show loading when filter is applied and when actually refetching
    isLocalLoading;

  useEffect(() => {
    if (startDate && endDate) {
      setIsLocalLoading(true);

      refetch().finally(() => {
        setIsLocalLoading(false);
      });
    }
  }, [startDate, endDate, refetch]);
  const legend = [
    {
      text: t("chart.average_load"),
      color: COLORS.BLACK,
    },
    {
      text: t("chart.minimum_load"),
      color: COLORS.YELLOW,
    },
    {
      text: t("chart.maximum_load"),
      color: COLORS.PURPLE,
    },
  ];
  return (
    <CardLayout
      title={title || t("chart.load_profile")}
      className="lg:min-w-[400px] flex-1 !min-h-[450px] "
    >
      <div className="flex flex-col h-full w-full relative !p-0 overflow-y-visible">
        {isLoading ? (
          <div className="flex justify-center items-center h-full w-full">
            <Loader />
          </div>
        ) : error ? (
          (error as any).response.data.detail ? (
            <NoDeviceCardError
              message={FEEDBACK_MESSAGES.NO_DEVICE}
              className="!max-h-[150px]"
            />
          ) : (
            <CardError
              message={FEEDBACK_MESSAGES.ERROR}
              className="!h-[100px]"
            />
          )
        ) : loadProfileData.length ? (
          <>
            <div className="flex gap-2 justify-between w-[150px]">
              {isPredicted && (
                <select className="cursor-pointer flex items-center justify-center py-1 px-2 rounded-full bg-[#E5E5E5] text-primary-blackMain text-[10px]">
                  {filter.map((item) => (
                    <option
                      key={item.key}
                      value={item.value}
                      className="text-primary-blackLight cursor-pointer"
                      selected={item.value === 7}
                      disabled={item.value === 30}
                    >
                      {item.key}
                    </option>
                  ))}
                </select>
              )}
              <div className="flex-1 flex gap-2 justify-end">
                {isAggregatedData && !isPredicted && 
                  legend.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-1 rounded-full bg-primary-blackLighter2 px-3 py-2"
                    >
                      <span
                        className="w-4 h-4 rounded-md"
                        style={{ backgroundColor: item.color }}
                      ></span>
                      <span className="text-[11px]">{item.text}</span>
                    </div>
                  ))}
              </div>
            </div>

            <ReactECharts
              option={options}
              style={{ height: "100%", overflowY: "visible" }}
            />
          </>
        ) : (
          <NoContent message={FEEDBACK_MESSAGES.NO_DATA} />
        )}
      </div>
    </CardLayout>
  );
};

export default LoadProfileChart;
